'use client';

import React from 'react';
import { Typography, Button } from '../kit';
import { STATUS, TEXT, BACKGROUND, BORDER } from '@/constants/colors';
import {
  Box,
  Card,
  Stack,
  Divider,
  CircularProgress,
  IconButton,
  alpha,
  Chip,
  Container,
  Grid,
  keyframes,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  LocationOn,
  ArrowForward,
  Edit as EditIcon,
  Scale,
  StraightenRounded,
  Timer,
  LocalShipping,
  Shield,
  Inventory2,
} from '@mui/icons-material';
import { useTranslation } from '@/kit/useTranslation';

// Subtle animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
`;

export default function BookingSummary({
  details = {},
  onBack,
  onConfirm,
  onEdit,
  loading = false,
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();

  const {
    origin,
    destination,
    parcelType = [],
    parcelWeight,
    estimatedPrice: price = 0,
    distance,
    weight,
    breakdown,
    units,
  } = details;

  const getAddressLabel = (location) => {
    if (!location || typeof location === 'string') return '';
    return location.savedAddressLabel || location.label || '';
  };

  const getAddressFullText = (location) => {
    if (!location) return t('booking.notSpecified');
    if (typeof location === 'string') return location;

    // Get the full address (API-saved addresses have savedAddressFullAddress or fullAddress)
    const fullAddress =
      location.savedAddressFullAddress || location.fullAddress || location.address || '';

    if (fullAddress) return fullAddress;

    // Fallback to component-based formatting
    const parts = [
      location.door,
      location.street,
      location.area,
      location.city,
      location.state,
      location.pincode,
    ].filter(Boolean);
    return parts.join(', ') || t('booking.locationFallback');
  };

  const formatAddress = (location) => {
    return getAddressFullText(location);
  };

  const truncateAddress = (address, maxLen = 60) => {
    const full = formatAddress(address);
    return full.length > maxLen ? full.substring(0, maxLen) + '...' : full;
  };

  // Use server-provided breakdown if available, else fallback to proportional estimate
  const displayBaseFare = breakdown?.baseRate || Math.round(price * 0.7);
  const displayServiceFee = breakdown?.marginAmount || Math.round(price * 0.1);
  const displayDistanceCharge = breakdown ? 0 : Math.round(price * 0.2);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 3,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            bgcolor: BACKGROUND.subtle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={28} sx={{ color: STATUS.info }} />
        </Box>
        <Typography sx={{ fontSize: '0.95rem', color: TEXT.secondary, fontWeight: 500 }}>
          {t('booking.processingBooking')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: BACKGROUND.subtle,
        pt: { xs: 2, md: 4 },
        pb: 12,
      }}
    >
      <Container maxWidth="md" sx={{ px: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: '1.5rem', md: '1.75rem' },
                fontWeight: 800,
                color: TEXT.primary,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              {t('booking.confirmBooking')}
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: TEXT.secondary, mt: 0.5 }}>
              {t('booking.reviewDetails')}
            </Typography>
          </Box>
          {onEdit && (
            <IconButton
              onClick={onEdit}
              sx={{
                bgcolor: BACKGROUND.subtle,
                color: TEXT.secondary,
                '&:hover': { bgcolor: BORDER.light },
              }}
            >
              <EditIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Stack>

        {/* Two-column layout on desktop */}
        <Grid container spacing={3}>
          {/* Left Column - Route Info */}
          <Grid item xs={12} md={7}>
            {/* Route Card */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${BORDER.light}`,
                overflow: 'hidden',
                mb: 2,
                animation: `${fadeIn} 0.4s ease-out`,
              }}
            >
              <Box sx={{ p: 2.5 }}>
                {/* Pickup */}
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor: STATUS.successLight,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: STATUS.success,
                        animation: `${pulseDot} 2s ease-in-out infinite`,
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        color: STATUS.success,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        mb: 0.25,
                      }}
                    >
                      {t('booking.pickup')}
                    </Typography>
                    {getAddressLabel(origin) && (
                      <Chip
                        label={getAddressLabel(origin)}
                        size="small"
                        sx={{
                          bgcolor: STATUS.successLight,
                          color: STATUS.success,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 26,
                          mb: 0.5,
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        color: TEXT.primary,
                        lineHeight: 1.4,
                        minHeight: 44,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {isDesktop ? formatAddress(origin) : truncateAddress(origin)}
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    ml: 2.25,
                    my: 1,
                    borderLeft: `2px dashed ${BORDER.light}`,
                    height: 24,
                  }}
                />

                {/* Delivery */}
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor: STATUS.errorLight,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <LocationOn sx={{ fontSize: 18, color: STATUS.error }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        color: STATUS.error,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        mb: 0.25,
                      }}
                    >
                      {t('booking.delivery')}
                    </Typography>
                    {getAddressLabel(destination) && (
                      <Chip
                        label={getAddressLabel(destination)}
                        size="small"
                        sx={{
                          bgcolor: STATUS.errorLight,
                          color: STATUS.error,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 26,
                          mb: 0.5,
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        color: TEXT.primary,
                        lineHeight: 1.4,
                        minHeight: 44,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {isDesktop ? formatAddress(destination) : truncateAddress(destination)}
                    </Typography>
                  </Box>
                </Stack>

                {/* Parcel Info Chips inside Route Card */}
                <Divider sx={{ my: 2 }} />
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  {parcelType?.slice(0, 2).map((type, idx) => (
                    <Chip
                      key={idx}
                      label={type}
                      size="small"
                      sx={{
                        bgcolor: BACKGROUND.subtle,
                        color: TEXT.secondary,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 28,
                        borderRadius: 2,
                      }}
                    />
                  ))}
                  {(parcelWeight || weight) && (
                    <Chip
                      icon={<Scale sx={{ fontSize: 14 }} />}
                      label={`${parcelWeight || weight} kg`}
                      size="small"
                      sx={{
                        bgcolor: BACKGROUND.subtle,
                        color: TEXT.secondary,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 28,
                        borderRadius: 2,
                        '& .MuiChip-icon': { color: TEXT.secondary },
                      }}
                    />
                  )}
                  {distance > 0 && (
                    <Chip
                      icon={<StraightenRounded sx={{ fontSize: 14 }} />}
                      label={`${distance} km`}
                      size="small"
                      sx={{
                        bgcolor: BACKGROUND.subtle,
                        color: TEXT.secondary,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 28,
                        borderRadius: 2,
                        '& .MuiChip-icon': { color: TEXT.secondary },
                      }}
                    />
                  )}
                  {units && units > 1 && (
                    <Chip
                      icon={<Inventory2 sx={{ fontSize: 14 }} />}
                      label={`${units} ${units === 1 ? t('booking.item') : t('booking.items')}`}
                      size="small"
                      sx={{
                        bgcolor: BACKGROUND.subtle,
                        color: TEXT.secondary,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 28,
                        borderRadius: 2,
                        '& .MuiChip-icon': { color: TEXT.secondary },
                      }}
                    />
                  )}
                  <Chip
                    icon={<Timer sx={{ fontSize: 14 }} />}
                    label={t('booking.estimatedTime')}
                    size="small"
                    sx={{
                      bgcolor: STATUS.pending.bg,
                      color: STATUS.pending.text,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 28,
                      borderRadius: 2,
                      '& .MuiChip-icon': { color: STATUS.pending.text },
                    }}
                  />
                </Stack>
              </Box>
            </Card>
          </Grid>

          {/* Right Column - Price Info */}
          <Grid item xs={12} md={5}>
            {/* Price Card */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${BORDER.light}`,
                overflow: 'hidden',
                mb: 2,
                animation: `${fadeIn} 0.4s ease-out 0.2s backwards`,
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ fontSize: '0.85rem', color: TEXT.secondary }}>
                      {t('booking.baseFare')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        color: TEXT.primary,
                      }}
                    >
                      ₹{displayBaseFare}
                    </Typography>
                  </Stack>
                  {displayDistanceCharge > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: '0.85rem', color: TEXT.secondary }}>
                        {t('booking.distanceCharge', { km: distance || 0 })}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: TEXT.primary,
                        }}
                      >
                        ₹{displayDistanceCharge}
                      </Typography>
                    </Stack>
                  )}
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ fontSize: '0.85rem', color: TEXT.secondary }}>
                      {t('booking.serviceFee')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        color: TEXT.primary,
                      }}
                    >
                      ₹{displayServiceFee}
                    </Typography>
                  </Stack>
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', color: TEXT.secondary }}>
                      {t('booking.totalAmount')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: TEXT.muted }}>
                      {t('booking.inclusiveOfTaxes')}
                    </Typography>
                  </Box>
                  <Typography
                    variant={'h5'}
                    pl={4}
                    // sx={{
                    //   fontSize: '1.5rem',
                    //   fontWeight: 'bold',
                    //   color: '#0f172a',
                    //   letterSpacing: '-0.02em',
                    //   pl: 4,
                    // }}
                  >
                    ₹{price}
                  </Typography>
                </Stack>
              </Box>
            </Card>

            {/* Trust badges */}
            <Stack
              direction="row"
              spacing={2}
              sx={{
                mb: 3,
                animation: `${fadeIn} 0.4s ease-out 0.3s backwards`,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <LocalShipping sx={{ fontSize: 16, color: STATUS.success, mr: 2 }} />
                <Typography sx={{ fontSize: '0.75rem', color: TEXT.secondary }}>
                  {t('booking.thirtyMinPickup')}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Shield sx={{ fontSize: 16, color: STATUS.info, mr: 2 }} />
                <Typography sx={{ fontSize: '0.75rem', color: TEXT.secondary }}>
                  {t('booking.insuredDelivery')}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        {/* Actions - Full width below grid */}
        <Stack spacing={1.5} sx={{ mt: 2, animation: `${fadeIn} 0.4s ease-out 0.4s backwards` }}>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={loading}
            fullWidth
            endIcon={!loading && <ArrowForward sx={{ fontSize: 18 }} />}
            sx={{
              py: 1.75,
              borderRadius: 2.5,
              fontSize: '0.95rem',
              fontWeight: 700,
              textTransform: 'none',
              bgcolor: TEXT.primary,
              color: TEXT.inverse,
              boxShadow: '0 4px 14px rgba(15, 23, 42, 0.25)',
              '&:hover': {
                bgcolor: '#1e293b',
                boxShadow: '0 6px 20px rgba(15, 23, 42, 0.35)',
              },
              '&:disabled': { bgcolor: BORDER.default },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: TEXT.inverse }} />
            ) : (
              t('booking.confirmAndBook')
            )}
          </Button>
          {onBack && (
            <Button
              variant="text"
              onClick={onBack}
              disabled={loading}
              fullWidth
              sx={{
                py: 1.25,
                borderRadius: 2,
                fontSize: '0.9rem',
                fontWeight: 600,
                textTransform: 'none',
                color: TEXT.secondary,
                '&:hover': { bgcolor: BACKGROUND.subtle },
              }}
            >
              {t('booking.goBack')}
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
